import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import { ColorModeProvider } from './app/ColorModeProvider';
import { AuthProvider } from './lib/auth-context';
import { ConfirmProvider } from './components/ConfirmDialog';
import { NotifyProvider } from './components/Notify';
import { router } from './app/router';
import { startPhotoQueue } from './lib/photo-queue';
import { startCountQueue } from './lib/count-queue';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // The api client already retries once behind a token refresh; retrying
      // an auth failure again would only burn requests.
      retry: (failureCount, error) =>
        failureCount < 2 && !(error as { status?: number })?.status?.toString().startsWith('4'),
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found');

startPhotoQueue();
startCountQueue();

createRoot(rootElement).render(
  <StrictMode>
    <ColorModeProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <NotifyProvider>
              <ConfirmProvider>
                <RouterProvider router={router} />
              </ConfirmProvider>
            </NotifyProvider>
          </AuthProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    </ColorModeProvider>
  </StrictMode>,
);
